import { Component, effect, ElementRef, inject, input, output, signal, ViewChild, AfterViewInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { QuillModule, QuillEditorComponent } from 'ngx-quill';
import { UIIconsComponent } from 'src/app/shared/ui/icons/icons.component';
import { AssetsApiService } from 'src/app/core/http/services/assets.service';
import { marked } from 'marked';

@Component({
  selector: 'app-free-form-text-editor',
  standalone: true,
  imports: [FormsModule, QuillModule, UIIconsComponent],
  templateUrl: './free-form-text-editor.component.html',
  styleUrl: './free-form-text-editor.component.css'
})
export class FreeFormTextEditorComponent implements AfterViewInit {
  cardId = input<string>('');
  initialContent = input<string>('');
  initialAssetId = input<string>('');
  isLoadingContent = input<boolean>(false);
  canvasId = input<string>('');
  cardTitle = input<string>('Free Form Card');

  @ViewChild('editorContainer', { static: false }) editorContainer!: ElementRef;
  @ViewChild('quillEditor', { static: false }) quillEditor!: QuillEditorComponent;
  @ViewChild('quillViewerEditor', { static: false }) quillViewerEditor!: QuillEditorComponent;

  assetCreated = output<{assetId: string, content: string}>();
  contentSaved = output<{assetId: string, content: string}>();
  saveStateChange = output<boolean>();
  openInsightsModal = output<void>();
  triggerCanvasSave = output<void>();

  editorState = signal<'empty' | 'editing' | 'viewing'>('empty');
  content = signal<string>('');
  assetId = signal<string>('');
  isSaving = signal<boolean>(false);
  saveError = signal<string>('');
  hasUnsavedChanges = signal<boolean>(false);

  private assetsApiService = inject(AssetsApiService);

  quillModules = {
    toolbar: {
      container: [
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote', 'code-block'],
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'script': 'sub'}, { 'script': 'super' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        [{ 'direction': 'rtl' }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        [{ 'font': [] }],
        [{ 'align': [] }],
        ['link', 'image', 'video'],
        ['clean']
      ],
      handlers: {
        'image': this.imageHandler.bind(this),
        'video': this.videoHandler.bind(this)
      }
    },
    clipboard: {
      matchVisual: false
    }
  };

  readOnlyModules = {
    toolbar: false,
    clipboard: {
      matchVisual: false
    }
  };

  quillInstance: any;

  constructor() {
    effect(() => {
      const isLoading = this.isLoadingContent();
      const initialContent = this.initialContent();
      const initialAssetId = this.initialAssetId();

      if (isLoading) {
        this.editorState.set('empty');
      } else if (initialContent && initialContent.trim() !== '') {
        this.content.set(initialContent);
        this.editorState.set('viewing');
      } else {
        this.editorState.set('editing');
      }

      if (initialAssetId && initialAssetId.trim() !== '') {
        this.assetId.set(initialAssetId);
      }
    }, { allowSignalWrites: true });
  }

  ngAfterViewInit(): void {
  }

  onPencilClick(): void {
    if (this.editorState() === 'empty') {
      this.editorState.set('editing');
      setTimeout(() => {
        this.focusEditor();
      }, 100);
    }
  }

  focusEditor(): void {
    if (this.quillEditor && this.quillEditor.quillEditor) {
      setTimeout(() => {
        try {
          this.quillEditor.quillEditor.enable(true);
          this.quillEditor.quillEditor.focus({ preventScroll: true });
          this.quillEditor.quillEditor.setSelection(0, 0);
          const editorElement = this.quillEditor.quillEditor.root;
          if (editorElement) {
            editorElement.scrollTop = 0;
          }
        } catch (error) {
          console.error('Error focusing editor:', error);
          try {
            this.quillEditor.quillEditor.focus();
            this.quillEditor.quillEditor.setSelection(0, 0);
          } catch (fallbackError) {
            console.error('Fallback focus also failed:', fallbackError);
          }
        }
      }, 100);
    } else {
      setTimeout(() => this.focusEditor(), 200);
    }
  }

  focusEditorAtEnd(): void {
    if (this.quillEditor && this.quillEditor.quillEditor) {
      setTimeout(() => {
        try {
          this.quillEditor.quillEditor.enable(true);
          this.quillEditor.quillEditor.focus({ preventScroll: true });
          const length = this.quillEditor.quillEditor.getLength();
          this.quillEditor.quillEditor.setSelection(length - 1, 0);
          const editorElement = this.quillEditor.quillEditor.root;
          if (editorElement) {
            editorElement.setAttribute('contenteditable', 'true');
            editorElement.style.pointerEvents = 'auto';
            editorElement.style.cursor = 'text';
            setTimeout(() => {
              editorElement.scrollTop = editorElement.scrollHeight;
            }, 50);
          }
        } catch (error) {
          console.error('Error focusing editor at end:', error);
          setTimeout(() => {
            try {
              this.quillEditor.quillEditor.enable(true);
              this.quillEditor.quillEditor.focus();
              const length = this.quillEditor.quillEditor.getLength();
              this.quillEditor.quillEditor.setSelection(length - 1, 0);
            } catch (fallbackError) {
              console.error('Fallback focus at end also failed:', fallbackError);
            }
          }, 100);
        }
      }, 150);
    } else {
      setTimeout(() => this.focusEditorAtEnd(), 200);
    }
  }

  onLightbulbClick(): void {
    this.openInsightsModal.emit();
  }

  async appendInsights(reportName: string, insights: string): Promise<void> {
    if (this.editorState() === 'viewing') {
      const formattedInsights = `\n\n--- Insights from ${reportName} ---\n\n${insights}`;
      this.content.update(c => c + formattedInsights);
      this.hasUnsavedChanges.set(true);
      this.saveStateChange.emit(true);
      return;
    }

    if (!this.quillEditor || !this.quillEditor.quillEditor) {
      this.startEditing();
      setTimeout(() => {
        this.appendInsights(reportName, insights);
      }, 200);
      return;
    }

    try {
      const editor = this.quillEditor.quillEditor;
      const currentLength = editor.getLength();
      const formattedContent = `\n--- Insights from ${reportName} ---\n\n`;
      editor.insertText(currentLength - 1, formattedContent);
      const htmlContent = await marked(insights);
      const range = this.quillInstance.getSelection();
      const index = range ? range.index : this.quillInstance.getLength();
      this.quillInstance.clipboard.dangerouslyPasteHTML(index, htmlContent);

      this.content.set(editor.root.innerHTML);
      this.hasUnsavedChanges.set(true);
      this.saveStateChange.emit(true);
      const newLength = editor.getLength();
      editor.setSelection(newLength - 1, 0);
    } catch (error) {
      console.error('Error appending insights to editor:', error);
    }
  }

  onSaveClick(): void {
    if (!this.content() || this.content().trim() === '' || this.content() === '<p><br></p>') {
      return;
    }

    this.isSaving.set(true);
    this.saveError.set('');

    const sanitizedContent = this.sanitizeHtml(this.content());

    const assetData = {
      ...(this.assetId() ? { id: this.assetId() } : {}),
      name: this.cardTitle() || 'Free Form Card',
      type: 'FREEFORM_CARD',
      status: 'OPEN',
      description: '',
      tags: [],
      parameters: {
        content: sanitizedContent
      }
    };

    const operation = this.assetId() ?
      this.assetsApiService.updateAsset(assetData) :
      this.assetsApiService.createAsset(assetData);

    operation.subscribe({
      next: (response: any) => {
        this.isSaving.set(false);
        const newAssetId = response?.data?.createAsset?.id || response?.data?.updateAsset?.id || this.assetId();

        if (newAssetId) {
          this.assetId.set(newAssetId);
          this.assetCreated.emit({ assetId: newAssetId, content: sanitizedContent });
          this.contentSaved.emit({ assetId: newAssetId, content: sanitizedContent });
        }

        this.content.set(sanitizedContent);
        this.hasUnsavedChanges.set(false);
        this.saveStateChange.emit(false);
        this.editorState.set('viewing');

        if (this.canvasId()) {
          setTimeout(() => {
            this.triggerCanvasSave.emit();
          }, 100);
        }
      },
      error: (err: Error) => {
        this.isSaving.set(false);
        this.saveError.set('Failed to save content. Please try again.');
        console.error('Error saving free-form card:', err);
      }
    });
  }

  startEditing(): void {
    if (this.editorState() === 'viewing' || this.editorState() === 'empty') {
      this.editorState.set('editing');
      setTimeout(() => {
        this.focusEditorAtEnd();
      }, 100);
    }
  }

  onEditorCreated(): void {
    if (this.quillEditor && this.quillEditor.quillEditor) {
      this.quillEditor.quillEditor.enable(true);
      this.quillInstance = this.quillEditor.quillEditor;
      const editorElement = this.quillEditor.quillEditor.root;
      if (editorElement) {
        editorElement.setAttribute('contenteditable', 'true');
        editorElement.style.pointerEvents = 'auto';
        editorElement.style.cursor = 'text';
        editorElement.style.userSelect = 'text';
        (editorElement.style as any).webkitUserSelect = 'text';

        editorElement.addEventListener('mousedown', (event: MouseEvent) => {
          event.stopPropagation();
        });
        editorElement.addEventListener('touchstart', (event: TouchEvent) => {
          event.stopPropagation();
        });
      }

      if (!this.content() || this.content().trim() === '' || this.content() === '<p><br></p>') {
        setTimeout(() => {
          this.focusEditor();
        }, 150);
      }
    }
  }

  get hasContent(): boolean {
    return !!(this.content() && typeof this.content() === 'string' && this.content().trim().length > 0 && this.content() !== '<p><br></p>');
  }

  get isSaved(): boolean {
    return !!this.assetId() && !this.hasUnsavedChanges();
  }

  get canSave(): boolean {
    return this.hasContent && !this.isSaving();
  }

  imageHandler(): void {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.style.display = 'none';

    input.onchange = () => {
      const file = input.files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const imageUrl = e.target?.result as string;
          if (this.quillEditor && this.quillEditor.quillEditor) {
            const range = this.quillEditor.quillEditor.getSelection();
            if (range) {
              this.quillEditor.quillEditor.insertEmbed(range.index, 'image', imageUrl);
              this.quillEditor.quillEditor.setSelection(range.index + 1, 0);
            }
          }
        };
        reader.readAsDataURL(file);
      }
    };

    document.body.appendChild(input);
    input.click();
    document.body.removeChild(input);
  }

  videoHandler(): void {
    const url = prompt('Enter video URL:');
    if (url) {
      if (this.quillEditor && this.quillEditor.quillEditor) {
        const range = this.quillEditor.quillEditor.getSelection();
        if (range) {
          this.quillEditor.quillEditor.insertEmbed(range.index, 'video', url);
          this.quillEditor.quillEditor.setSelection(range.index + 1, 0);
        }
      }
    }
  }

  private sanitizeHtml(html: string): string {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    const scripts = tempDiv.querySelectorAll('script');
    scripts.forEach(script => script.remove());

    const allElements = tempDiv.querySelectorAll('*');
    allElements.forEach(element => {
      const attributesToRemove = [];
      for (let i = 0; i < element.attributes.length; i++) {
        const attr = element.attributes[i];
        if (attr.name.startsWith('on') ||
            (attr.name === 'href' && attr.value.startsWith('javascript:'))) {
          attributesToRemove.push(attr.name);
        } else if (attr.name === 'style' &&
                   (attr.value.includes('javascript:') ||
                    attr.value.includes('expression(') ||
                    attr.value.includes('url(javascript:'))) {
          attributesToRemove.push(attr.name);
        } else if (attr.name === 'class') {
          continue;
        }
      }
      attributesToRemove.forEach(attr => element.removeAttribute(attr));
    });

    return tempDiv.innerHTML;
  }
}
